import { Fragment, useId } from 'react';
import { X } from 'lucide-react';
import { Modal } from '../../Modal';
import { useTranslation } from '../../../lib/i18n';
import { Button, IconButton, type OverflowMenuAction } from '../ui';
import styles from './FileActionsSheet.module.css';

// THE phone's row-actions menu (Figma `15910:425995` "Card/Medium"): a bottom sheet carrying the file's
// name and the same actions the desktop popover lists.
//
// **It is the same callbacks and the same trigger — only the presentation differs.** A popover anchored
// to a 26px kebab is a pointer idiom: on a phone it lands under a thumb, it has no room for the file's
// name, and it is not what either native app draws. `FileRowMenu` picks between the two off
// the `narrowPane` boolean the list hands down, in the same spirit as the app's Task/Skills detail split
// (those two read the VIEWPORT; this one reads the PANE, because the Files pane is what folds).
//
// The one item the popover does NOT list is GRANT ACCESS ("Share"), last in this sheet's primary group,
// immediately before the divider — the slot iOS (`DocumentViewer.swift:573`) and Android
// (`FileActionMenu.kt:200`) give it. The wide row says that action with a dedicated share glyph (§7); a
// narrow row hides the glyph, so without this entry the access dialog was unreachable on mobile web.
// The gate lives in `FilesBrowser` (one `useContainerNarrow` on the pane), not in this component.
//
// The shell is `Modal presentation="sheet"`, NOT a sheet primitive of its own: the scrim, the shared
// Escape layer (`lib/modal-escape.ts`), the rail-inert catcher and the portal are all already there, and
// a second copy of them is the drift orion-web/CLAUDE.md warns about.
//
// ⚠️ IT MUST BE RENDERED OUTSIDE THE ROW, not inside it. A portal does not escape React's event
// bubbling: a press on the scrim inside a row's subtree would bubble to the row and open the file
// underneath the sheet the user was dismissing. `FilesTable` / `FilesGrid` hold one of these per list,
// beside their rows, for the same reason `FilesBrowser` holds its dialogs beside the browser.
export function FileActionsSheet({
  open,
  onClose,
  title,
  groups,
}: {
  open: boolean;
  onClose: () => void;
  /** The file's own name — the sheet's heading, which the popover has no room to say. */
  title: string;
  /** The same `OverflowMenuAction[][]` the popover renders; a divider falls BETWEEN groups only. */
  groups: OverflowMenuAction[][];
}) {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <Modal open={open} onClose={onClose} presentation="sheet" hideClose labelledBy={titleId}>
      {/* The header BLOCK: the title row over the items, 16 apart. The 20 down to the bottom slot is
          the sheet's own content gap. */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.title} id={titleId}>
            {title}
          </span>
          {/* The sheet draws its OWN dismiss (24px, trailing edge of the title row), which is why the
              shell's floating ✕ is suppressed — two of them would just be doubled. */}
          <IconButton size="medium" aria-label={t('common.close')} onClick={onClose}>
            <X size={24} aria-hidden />
          </IconButton>
        </div>

        <div className={styles.items} role="menu" aria-labelledby={titleId}>
          {groups.filter((group) => group.length > 0).map((group, groupIndex) => (
            <Fragment key={groupIndex}>
              {groupIndex > 0 && <hr className={styles.divider} role="separator" />}
              {group.map((action) => {
                const content = action.label;
                const common = {
                  variant: 'text' as const,
                  size: 'medium' as const,
                  color: action.destructive ? ('destructive' as const) : ('standard' as const),
                  fullWidth: true,
                  className: styles.item,
                  role: 'menuitem',
                  /**
                   * ⚠️ THE GLYPH IS THE BUTTON'S `startIcon`, NOT part of its children — this is what puts
                   * the Figma's 4 between the mark and the label.
                   *
                   * `Button` wraps `children` in ONE inline-block `<span class="label">`, so an icon passed
                   * inside them becomes a sibling of the text INSIDE that span: the button's own
                   * `gap: 4` (DS `size-medium`) then applies between the span and nothing, the span itself
                   * computes `gap: normal`, and the 16px glyph renders flush against the first letter —
                   * measured at 0px, reported with a screenshot. As `startIcon` the glyph is a real flex
                   * child of the button, which is the only arrangement the gap can act on.
                   */
                  startIcon: action.icon,
                };
                if (action.href) {
                  return (
                    // A plain `<a>`, never a same-origin `<Link>` — an href here always opens a NEW
                    // document (see `OverflowMenuAction.href`), so Next's routing has nothing to do and
                    // its prefetch would be wasted. `external` is what makes the DS Button render one
                    // without rewriting the already-resolved, mount-prefixed URL.
                    <Button
                      {...common}
                      key={action.key}
                      href={action.href}
                      external
                      target={action.external || action.newTab ? '_blank' : undefined}
                      rel={action.external || action.newTab ? 'noopener noreferrer' : undefined}
                      onClick={() => {
                        onClose();
                        action.onSelect?.();
                      }}
                    >
                      {content}
                    </Button>
                  );
                }
                return (
                  <Button
                    {...common}
                    key={action.key}
                    onClick={() => {
                      onClose();
                      action.onSelect?.();
                    }}
                  >
                    {content}
                  </Button>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      {/* The bottom slot: 42 of clearance under the last action, for the phone's own home indicator.
          A real box rather than padding, because the sheet's content column states its 20 gap and this
          is the thing that gap is measured to. */}
      <div className={styles.safeArea} aria-hidden />
    </Modal>
  );
}
